import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { showToast, toastPromise } from '../utils/toast'

export interface Profile {
  id: string
  username: string | null
  full_name: string | null
  bio: string | null
  avatar_url: string | null
  location: string | null
  is_expert: boolean
  health_goals: string[]
  interests: string[]
  created_at: string
  updated_at: string
  // Expert following and session reservations
  following_experts?: string[]
  reserved_sessions?: Array<{
    sessionId: string
    sessionTitle: string
    reservedAt: string
    reminderSet: boolean
  }>
}

export function useProfile() {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  const fetchProfile = async () => {
    if (!user) {
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) throw error
      
      // Load additional data from localStorage for demo purposes
      const followingExperts = JSON.parse(localStorage.getItem('following-experts') || '[]')
      const reservedSessions = JSON.parse(localStorage.getItem('user-reservations') || '[]')
      
      setProfile({
        ...data,
        following_experts: followingExperts,
        reserved_sessions: reservedSessions
      })
    } catch (error) {
      console.error('Error fetching profile:', error)
      showToast.system.error('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProfile()
  }, [user])

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) {
      showToast.auth.authError('Please sign in to update profile')
      return { error: 'Not authenticated' }
    }

    const updateProfilePromise = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error
      
      // Update local state with localStorage data
      const followingExperts = JSON.parse(localStorage.getItem('following-experts') || '[]')
      const reservedSessions = JSON.parse(localStorage.getItem('user-reservations') || '[]')
      
      const updatedProfile = {
        ...data,
        following_experts: followingExperts,
        reserved_sessions: reservedSessions
      }
      
      setProfile(updatedProfile)
      return updatedProfile
    }

    try {
      await toastPromise(updateProfilePromise(), {
        loading: 'Updating your profile...',
        success: 'Profile updated successfully! ✨',
        error: 'Failed to update profile. Please try again.'
      })
      
      return { error: null }
    } catch (error) {
      console.error('Error updating profile:', error)
      return { error }
    }
  }

  const addFollowingExpert = (expertId: string) => {
    const currentFollowing = JSON.parse(localStorage.getItem('following-experts') || '[]')
    const updatedFollowing = [...new Set([...currentFollowing, expertId])]
    localStorage.setItem('following-experts', JSON.stringify(updatedFollowing))
    
    if (profile) {
      setProfile({
        ...profile,
        following_experts: updatedFollowing
      })
    }
  }

  const removeFollowingExpert = (expertId: string) => {
    const currentFollowing = JSON.parse(localStorage.getItem('following-experts') || '[]')
    const updatedFollowing = currentFollowing.filter((id: string) => id !== expertId)
    localStorage.setItem('following-experts', JSON.stringify(updatedFollowing))
    
    if (profile) {
      setProfile({
        ...profile,
        following_experts: updatedFollowing
      })
    }
  }

  const addSessionReservation = (reservation: {
    sessionId: string
    sessionTitle: string
    reservedAt: string
    reminderSet: boolean
  }) => {
    const currentReservations = JSON.parse(localStorage.getItem('user-reservations') || '[]')
    const updatedReservations = [...currentReservations, reservation]
    localStorage.setItem('user-reservations', JSON.stringify(updatedReservations))
    
    if (profile) {
      setProfile({
        ...profile,
        reserved_sessions: updatedReservations
      })
    }
  }

  return {
    profile,
    loading,
    updateProfile,
    refreshProfile: fetchProfile,
    addFollowingExpert,
    removeFollowingExpert,
    addSessionReservation
  }
}